import { apiClient } from '@/lib/apiClient';
import type { QueryOptions } from '@/lib/reactQuery';
import type { Pagination } from '@/types/api';
import type { Appointment } from '@/types/scheduling';
import { useQuery } from '@tanstack/react-query';

export interface GetUpcomingAppointmentsApiResponseData {
  appointments: Appointment[];
  pagination: Pagination;
}

export interface GetUpcomingAppointmentsResponseData {
  data: {
    appointments: Appointment[];
  };
  pagination: Pagination;
}

/**
 * `page`/`pageSize` drive count-based pagination (the full "Your sessions" list).
 * When `days` is provided (home-screen preview), the API instead returns every
 * session across the next N distinct days that have sessions, grouped by `timeZone`
 * so the day boundaries match what the guardian sees.
 */
export interface GetUpcomingAppointmentsParams {
  page?: number;
  pageSize?: number;
  days?: number;
  timeZone?: string;
}

export const getUpcomingAppointments = async (params: GetUpcomingAppointmentsParams): Promise<GetUpcomingAppointmentsResponseData> => {
  const response = await apiClient.get<GetUpcomingAppointmentsApiResponseData>('/v1/scheduling/appointments/upcoming', { params });
  return {
    data: {
      appointments: response.data.appointments,
    },
    pagination: response.data.pagination,
  };
};

interface UseGetUpcomingAppointmentsOptions extends GetUpcomingAppointmentsParams {
  options?: QueryOptions<GetUpcomingAppointmentsResponseData>;
}

export const GET_UPCOMING_APPOINTMENTS_QUERY_KEY = 'getUpcomingAppointments';

export const getUpcomingAppointmentsQueryConfig = ({ page, pageSize, days, timeZone }: GetUpcomingAppointmentsParams) => ({
  queryKey: [GET_UPCOMING_APPOINTMENTS_QUERY_KEY, page, pageSize, days ?? null, timeZone ?? null],
  queryFn: () => getUpcomingAppointments({ page, pageSize, days, timeZone }),
});

export const useGetUpcomingAppointments = ({ page, pageSize, days, timeZone, options }: UseGetUpcomingAppointmentsOptions) => {
  return useQuery({
    ...getUpcomingAppointmentsQueryConfig({ page, pageSize, days, timeZone }),
    ...options,
  });
};
